import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";

/**
 * Holds the signed-in admin's identity and resolved feature permissions.
 *
 * The permission map comes from `GET /api/admin/me` rather than localStorage,
 * so revoking someone's access takes effect on their next page load instead of
 * waiting for them to log out. localStorage is only a first-paint fallback.
 */
const PermissionContext = createContext({
  admin: null,
  permissions: {},
  loading: true,
  can: () => false,
  canAny: () => false,
  submitLabel: () => "Submit",
  refresh: () => {},
});

const storedUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || null;
  } catch {
    return null;
  }
};

export const PermissionProvider = ({ children }) => {
  const cached = storedUser();

  const [admin, setAdmin] = useState(cached);
  const [permissions, setPermissions] = useState(cached?.permissions || {});
  const [loading, setLoading] = useState(Boolean(cached?.token));

  const refresh = useCallback(async () => {
    const user = storedUser();
    if (!user?.token) {
      setAdmin(null);
      setPermissions({});
      setLoading(false);
      return;
    }

    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_ROOT}/api/admin/me`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      if (data?.status && data.data) {
        setAdmin({ ...user, ...data.data });
        setPermissions(data.data.permissions || {});
        // Keep the cache in step so the next first paint is correct.
        localStorage.setItem("user", JSON.stringify({ ...user, ...data.data }));
      }
    } catch (error) {
      // A 401 means the session was revoked or expired — the existing session
      // popup handles the redirect, so just fall back to no permissions.
      if (error?.response?.status === 401) {
        setPermissions({});
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // The provider mounts once, at app start — before anyone has logged in. Login
  // writes `user` to localStorage and navigates client-side, so without this
  // the context keeps its pre-login (empty) permissions and the sidebar renders
  // only the Dashboard until the operator manually refreshes the page.
  // `storage` covers other tabs; `penspace:auth` is dispatched by the login
  // screen for this one, since `storage` does not fire in the writing tab.
  useEffect(() => {
    const onAuthChange = () => refresh();
    window.addEventListener("storage", onAuthChange);
    window.addEventListener("penspace:auth", onAuthChange);
    return () => {
      window.removeEventListener("storage", onAuthChange);
      window.removeEventListener("penspace:auth", onAuthChange);
    };
  }, [refresh]);

  const can = useCallback(
    (feature, action) => (permissions?.[feature] || []).includes(action),
    [permissions]
  );

  /** True when the admin has any access at all to a feature. */
  const canAny = useCallback(
    (feature) => (permissions?.[feature] || []).length > 0,
    [permissions]
  );

  /**
   * Label for a create form's primary button. Without `publish` the API saves
   * the record as a draft (status 0), so the button must not promise more than
   * it delivers.
   */
  const submitLabel = useCallback(
    (feature) => (can(feature, "publish") ? "Publish" : "Save as draft"),
    [can]
  );

  const value = useMemo(
    () => ({ admin, permissions, loading, can, canAny, submitLabel, refresh }),
    [admin, permissions, loading, can, canAny, submitLabel, refresh]
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => useContext(PermissionContext);

/**
 * Renders [children] only when the admin holds the given right.
 *
 *   <Can feature="books" action="delete"><DeleteButton /></Can>
 */
export const Can = ({ feature, action, fallback = null, children }) => {
  const { can } = usePermissions();
  return can(feature, action) ? children : fallback;
};
